import { useMemo, useEffect } from 'react';
import moment from 'moment';
import { uniq, last, times } from 'ramda';

import {
  usePlanningActions,
  useAlerts,
  ID,
  DATE,
  SHIFTS,
  SUB_ORGANIZATION,
  LIMIT,
  CURSOR,
  FIELDS,
  EXPAND,
  SORT_BY,
  SORT_DIR,
  DESC,
  FROM,
  TO,
  STATUS,
} from 'store/planning';
import { CANTON, NAME } from 'store/organizations';
import { STATUSES } from '../../consts';

import { ONE_DAY, getDateKey } from './utils';

const getShiftRanges = (range) =>
  range.reduce(
    (acc, date) => {
      const week = moment(date).week();

      if (acc.items.length === 0 || acc.week !== week) {
        acc.items.push([date]);
        acc.week = week;

        return acc;
      }

      const lastDate = last(last(acc.items));
      const diff = Math.abs(moment(date).valueOf() - moment(lastDate).valueOf());

      if (diff > ONE_DAY) {
        acc.items.push([date]);
      } else {
        last(acc.items).push(date);
      }

      return acc;
    },
    { items: [], week: 0 }
  ).items;

const combineStrings = (str1, str2) => {
  let result = '';

  for (let i = 0; i < str1.length; i += 1) {
    if (str2[i] === '1' && str1[i] === str2[i]) {
      result = null;
    } else if (result === null) {
      i = str1.length;
    } else {
      result += str1[i] === '1' || str2[i] === '1' ? '1' : '0';
    }
  }

  return result;
};
const rowCreator = (columnCount) => {
  let [list, index, isFound] = [['0'.repeat(columnCount)], null, false];

  return (start, length) => {
    index = null;
    isFound = false;
    const str = times((i) => Number(i >= start && i < start + length), columnCount).join('');

    list = list.map((tmp, i) => {
      if (isFound) return tmp;

      const next = combineStrings(tmp, str);

      if (!next) return tmp;

      isFound = true;
      index = i;

      return next;
    });

    if (index === null) {
      list.push(combineStrings('0'.repeat(columnCount), str));

      return list.length - 1;
    }

    return index;
  };
};

const handleScheduleList = (days, schedule, columnCount) => {
  const scheduleData = schedule.reduce(
    (acc, { [ID]: id, [SHIFTS]: shifts, [SUB_ORGANIZATION]: organization, [STATUS]: status }) => {
      const range = uniq(shifts.map(([d]) => getDateKey(d)));

      getShiftRanges(range).forEach((items) => {
        acc.push({
          id,
          range: items,
          name: organization[NAME],
          canton: organization[CANTON],
          color: STATUSES[status]?.color,
          status,
        });
      });

      return acc;
    },
    []
  );

  const getRowByWeek = {};

  return days.map(({ key, index, weekIndex = 0 }) =>
    scheduleData.reduce((acc, { id, range, name, canton, color, status }) => {
      if (range[0] === key) {
        if (!getRowByWeek[weekIndex]) getRowByWeek[weekIndex] = rowCreator(columnCount);

        const daysCount = Math.min(range.length, columnCount - index);

        acc.push({
          id,
          column: index,
          row: getRowByWeek[weekIndex](index, daysCount),
          weekIndex,
          name,
          canton,
          days: daysCount,
          color,
          status,
        });
      }

      return acc;
    }, [])
  );
};

export const useScheduleLines = (days, columnCount) => {
  const { fetchCalenderSchedules } = usePlanningActions();
  const { action, loading, success, error } = useAlerts(fetchCalenderSchedules);
  const list = useMemo(() => (success ? handleScheduleList(days, success, columnCount) : []), [columnCount, days, success]);
  const [from, to] = useMemo(
    () => [days[0].date.toISOString(), new Date(last(days).date.valueOf() + ONE_DAY - 1).toISOString()],
    [days]
  );

  useEffect(() => {
    action({
      [LIMIT]: 100,
      [CURSOR]: 0,
      [FIELDS]: [DATE, SUB_ORGANIZATION, SHIFTS, FROM, TO, STATUS],
      [EXPAND]: [SUB_ORGANIZATION],
      [SORT_BY]: DATE,
      [SORT_DIR]: DESC,
      [FROM]: from,
      [TO]: to,
    });
  }, [action, from, to]);

  return { list, loading, error };
};
